import {
  RegulationRequestTypeDto,
  SimplifiedRegionRequestTypeDto,
} from "../../common/service/server/dto/RegulationRequestTypeDto";

export interface LocationOption {
  label: string;
  value: string;
  children?: LocationOption[];
}

export interface LocationOptionsResult {
  locations: LocationOption[];
  showCountrySelector: boolean;
}

/**
 * Helper function to check if a state notice is hidden
 * Adapted from privacy center implementation
 */
const isStateNoticeHidden = (
  hiddenStateSectionAnchors: string[] = [],
  stateAbbr: string,
): boolean => {
  return hiddenStateSectionAnchors.includes(stateAbbr);
};

/**
 * Create hierarchical location options from regulation request types and region mapping
 * This mirrors the exact logic used in the privacy center for consistency
 */
export const createLocationOptions = (
  dataRequestTypes: RegulationRequestTypeDto[],
  regionRequestTypeMap: Record<string, SimplifiedRegionRequestTypeDto>,
  hiddenStateSectionAnchors: string[] = [],
): LocationOptionsResult => {
  if (!dataRequestTypes || dataRequestTypes.length === 0 || !regionRequestTypeMap) {
    return {
      locations: [],
      showCountrySelector: false,
    };
  }

  // Helper function to get region name from the available properties
  const getRegionName = (regionCode: string, regulation: string) => {
    // Look for the region in regionRequestTypeMap by exact region code match first
    const regionEntry = Object.entries(regionRequestTypeMap).find(
      ([code, mapping]) => code === regionCode && mapping.regulation === regulation,
    );
    if (regionEntry && regionEntry[1]) {
      const mapping = regionEntry[1];
      // Use regionName from the mapping
      return mapping.regionName;
    }
    return regulation;
  };

  // Get unique regulations from dataRequestTypes
  const availableRegulations = new Set(
    dataRequestTypes.map((requestType) => requestType.regulationLaw.abbreviation),
  );

  // Helper function to get state abbreviation from region code
  const getStateAbbreviation = (regionCode: string) => {
    // Extract state abbreviation from US region codes like "US-CA" -> "CA"
    if (regionCode.startsWith("US-")) {
      return regionCode.substring(3);
    }
    return null;
  };

  const usLocations: LocationOption[] = [];
  const locations: LocationOption[] = [];
  let showCountrySelector = false;

  // Create a simplified mapping of regulations and their respective locations
  const regulationToLocations: Record<string, string[]> = {};
  Object.entries(regionRequestTypeMap).forEach(([regionCode, mapping]) => {
    const regulation = mapping.regulation;
    // Skip if this regulation is not available in dataRequestTypes
    if (!availableRegulations.has(regulation)) {
      return;
    }

    if (!regulationToLocations[regulation]) {
      regulationToLocations[regulation] = [];
    }

    // Check if it's a US state regulation and not hidden
    const stateAbbr = getStateAbbreviation(regionCode);
    if (stateAbbr && isStateNoticeHidden(hiddenStateSectionAnchors, stateAbbr)) {
      return;
    }

    const regionName = getRegionName(regionCode, regulation);
    regulationToLocations[regulation].push(regionName);
  });

  // Handle US state regulations - dynamically determined from backend data
  // Get all US state regulations from regionRequestTypeMap (regions starting with "US-")
  const usStateRegulations = new Set<string>();
  Object.entries(regionRequestTypeMap).forEach(([regionCode, mapping]) => {
    if (
      regionCode.startsWith("US-") &&
      mapping.regulation &&
      availableRegulations.has(mapping.regulation)
    ) {
      usStateRegulations.add(mapping.regulation);
    }
  });

  usStateRegulations.forEach((regulation) => {
    if (regulationToLocations[regulation]) {
      // Find the region code for this regulation instead of using regulation name as value
      Object.entries(regionRequestTypeMap).forEach(([regionCode, mapping]) => {
        if (mapping.regulation === regulation && !regionCode.startsWith("OTHER")) {
          const regionName = getRegionName(regionCode, regulation);
          usLocations.push({
            label: regionName,
            value: regionCode, // Use region code instead of regulation abbreviation
          });
        }
      });
    }
  });

  // Add VOLUNTARY states to US dropdown
  if (regulationToLocations["VOLUNTARY"]) {
    Object.entries(regionRequestTypeMap).forEach(([regionCode, mapping]) => {
      if (mapping.regulation === "VOLUNTARY") {
        const regionName = getRegionName(regionCode, "VOLUNTARY");
        // Skip if this is a duplicate (already added by state regulation logic above)
        const alreadyExists = usLocations.some((loc) => loc.value === regionCode);
        if (!alreadyExists) {
          usLocations.push({
            label: regionName,
            value: regionCode, // Use region code (e.g., "US-AK", "US-AL", "OTHER") as value
          });
        }
      }
    });
  }

  // Helper function to get region group label
  const getRegionGroupLabel = (regionGroupKey: string) => {
    // For now, return the key itself - can be enhanced with i18n later
    switch (regionGroupKey) {
      case "region_group.united_states":
        return "United States";
      case "region_group.eea_uk":
        return "Europe/UK";
      case "region_group.canada":
        return "Canada";
      case "region_group.other":
        return "Other";
      default:
        return regionGroupKey;
    }
  };

  // Add United States parent location if we have state locations
  if (usLocations.length > 0) {
    // Sort alphabetically, but keep "Other" at the end if it exists
    const sortedUsLocations = usLocations
      .filter((loc) => loc.value !== "OTHER")
      .sort((a, b) => a.label.localeCompare(b.label));

    // Add "Other" option at the end if it exists
    const otherLocation = usLocations.find((loc) => loc.value === "OTHER");
    if (otherLocation) {
      sortedUsLocations.push(otherLocation);
    }

    locations.push({
      label: getRegionGroupLabel("region_group.united_states"),
      value: "USA",
      children: sortedUsLocations,
    });
  }

  // Handle GDPR - use region group approach like privacy-center form
  if (regulationToLocations["GDPR"]) {
    locations.push({
      label: getRegionGroupLabel("region_group.eea_uk"),
      value: "EEA_UK", // Use region group identifier instead of regulation name
    });
    showCountrySelector = true;
  }

  // Handle PIPEDA - use region group approach like privacy-center form
  if (regulationToLocations["PIPEDA"]) {
    locations.push({
      label: getRegionGroupLabel("region_group.canada"),
      value: "CANADA", // Use region group identifier instead of regulation name
    });
    showCountrySelector = true;
  }

  // Handle VOLUNTARY - use region group approach like privacy-center form
  if (regulationToLocations["VOLUNTARY"]) {
    locations.push({
      label: getRegionGroupLabel("region_group.other"),
      value: "OTHER", // Use region group identifier instead of regulation name
    });
    showCountrySelector = true;
  }

  return {
    locations: locations,
    showCountrySelector,
  };
};
